import React, { useEffect, useState } from 'react';

import { CustomModal } from '../../BrandCampaign/BrandCampaignHero';
import { PrimaryLink } from '../Links';
import * as styles from './BlindForm.module.scss';
import EmbeddedMarketoForm from './EmbeddedMarketoForm';
import { BLIND_FORM_ID, useLeadFormEffect } from './formEffects';

const BlindForm = ({
  onBlindSuccess,
  autoSubmit = false,
  accessPrompt,
  accessCtaLabel = 'Access Content',
  cta,
  fallbackFormId,
  children,
}) => {
  const [thinking, setThinking] = useState(true);
  const [useBackupForm, setUseBackupForm] = useState(false);
  const [showAccess, setShowAccess] = useState(false);
  const [blindSubmitting, setBlindSubmitting] = useState(false);
  const [formInstance, setFormInstance] = useState(null);
  const [open, setOpen] = useState(false);

  const ctaData = { ...cta, openContentInModal: false, modalContent: null };

  const { form, displayGate, lead, leadFailed } = useLeadFormEffect({
    fallbackFormId,
  });

  // Extract onSuccess from children props
  const childOnSuccess = React.Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.props.onSuccess
  )?.props.onSuccess;

  const onBlindSubmit = async (data) => {
    if (!formInstance) return;

    setBlindSubmitting(true);
    try {
      await formInstance.submit();
      // Call the child's onSuccess function if it exists
      if (childOnSuccess) await childOnSuccess(data);
      if (onBlindSuccess) await onBlindSuccess(data);
    } catch (error) {
      if (typeof window !== 'undefined' && window.FS) {
        window.FS.event('Marketo Form', {
          type: 'Blind form submit failed',
          formID: BLIND_FORM_ID,
        });
      }
      console.log('Error submitting form:', error);
    } finally {
      setBlindSubmitting(false);
    }
  };

  useEffect(() => {
    //console.log(`BlindForm useEffect displayGate: ${displayGate}, leadFailed: ${leadFailed}, lead: ${!!lead}`);

    // If we should display the gate (no lead found) or lead detection failed, show backup form
    if (displayGate || leadFailed) {
      setThinking(false);
      setUseBackupForm(true);
    }

    // If we have a lead and shouldn't display gate, show access button
    if (!leadFailed && lead && !displayGate) {
      setThinking(false);
      setShowAccess(true);
    }

    if (lead && formInstance && !displayGate && autoSubmit) {
      const submit = async () => {
        setThinking(true);
        setBlindSubmitting(true);
        try {
          await onBlindSubmit({
            Email: lead.email,
            FirstName: lead.firstName,
            LastName: lead.lastName,
            LeadId: lead.id,
          });
        } catch (e) {
          console.log('Error auto-submitting form:', e);
          setShowAccess(false);
          setUseBackupForm(true);
        } finally {
          setThinking(false);
          setBlindSubmitting(false);
        }
      };
      submit();
    }
  }, [displayGate, leadFailed, lead, formInstance, autoSubmit]);

  const onRendered = async (marketoForm) => {
    // Check if we should display gate before setting values
    if (displayGate) return;

    // Ensure marketoForm exists and has setValues method
    if (!marketoForm || typeof marketoForm.setValues !== 'function') {
      console.log('MarketoForm instance not ready or missing setValues method');
      return;
    }

    setFormInstance(marketoForm);

    // Only set values if we have lead data
    if (lead) {
      try {
        marketoForm.setValues({
          Email: lead.email,
          FirstName: lead.firstName,
          LastName: lead.lastName,
          LeadId: lead.id,
        });
      } catch (error) {
        console.log('Error setting form values:', error);
      }
    }
  };

  const onFormSuccess = (vals, formId) => {
    if (typeof window !== 'undefined' && window.FS) {
      window.FS.event('Marketo Form', {
        type: 'Form submitted',
        formID: formId,
      });
    }
    // Call the child's onSuccess when the blind form succeeds
    if (childOnSuccess) {
      childOnSuccess();
    }
  };

  let innerModal = null;

  // Show loading spinner while thinking and not displaying gate
  if (thinking && !displayGate) {
    innerModal = (
      <React.Fragment>
        <span key="loading-spinner" className={styles.spinner} />
        <div className={styles.hidden}>
          <EmbeddedMarketoForm
            onSuccess={(vals) => {
              onFormSuccess(vals, BLIND_FORM_ID);
            }}
            onRendered={onRendered}
            formId={BLIND_FORM_ID}
          />
        </div>
      </React.Fragment>
    );
  }

  // Show access button if lead exists and can access content
  if (showAccess) {
    innerModal = (
      <div>
        {accessPrompt && <div className={styles.accessPrompt}>{accessPrompt}</div>}
        <PrimaryLink
          className={blindSubmitting ? styles.disabled : ''}
          isButton={true}
          label={blindSubmitting ? 'Processing...' : accessCtaLabel}
          onClick={onBlindSubmit}
          disabled={blindSubmitting}
        />
      </div>
    );
  }

  // Show backup form if lead detection failed or gate should be displayed
  innerModal = <div className={useBackupForm ? '' : styles.hidden}>{useBackupForm && children}</div>;

  return (
    <CustomModal className={[styles.modal].join(' ')} open={open} onClose={() => setOpen(false)}>
      {innerModal}
    </CustomModal>
  );
};

export default BlindForm;
